// Transformation Card Component
// Date: December 4, 2025
// Purpose: Card view for Transformation entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RocketLaunchIcon,
  CalendarIcon,
  FlagIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface TransformationCardProps {
  transformation: {
    id: string;
    name: string;
    description?: string;
    status?: string;
    hierarchy_type?: string;
    industry?: string;
    functional_area?: string;
    start_date?: string;
    end_date?: string;
    completion_percentage?: number;
    inchstone_stats?: {
      total_inchstones: number;
      completed_inchstones: number;
      delayed_inchstones: number;
      completion_percentage: number;
    };
    [key: string]: any;
  };
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onCopy?: (item: any) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: any) => boolean;
  editActionIcon?: React.ComponentType<any>;
  editActionLabel?: string;
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
  }>;
}

export const TransformationCard: React.FC<TransformationCardProps> = ({
  transformation,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  editActionIcon,
  editActionLabel,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (!transformation.id) {
      console.error('Transformation ID is missing');
      return;
    }
    
    if (onClick) {
      onClick(transformation.id);
    } else if (onView) {
      // onView expects the full item object, not just the ID
      onView(transformation);
    } else {
      navigate(`/transformations/${transformation.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEdit) {
      onEdit(transformation);
    } else {
      navigate(`/transformations/${transformation.id}/edit`);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'IN_PROGRESS':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      case 'PLANNED':
      case 'NOT_STARTED':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'ON_HOLD':
      case 'PAUSED':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'COMPLETED':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      case 'CANCELLED':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const completionPercentage = transformation.completion_percentage || 
    transformation.inchstone_stats?.completion_percentage || 0;
  const hasDelayedInchstones = (transformation.inchstone_stats?.delayed_inchstones || 0) > 0;

  return (
    <Card 
      variant="outline" 
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* First Line: Icon and Badges */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <RocketLaunchIcon className="w-5 h-5 text-[var(--color-info)] flex-shrink-0" />
            {transformation.hierarchy_type && (
              <Badge variant="outline" className="text-xs">
                {transformation.hierarchy_type}
              </Badge>
            )}
            {transformation.status && (
              <Badge className={`text-xs border ${getStatusColor(transformation.status)}`}>
                {transformation.status}
              </Badge>
            )}
          </div>
        </div>

        {/* Second Line: Name */}
        <h3 className="font-semibold text-[var(--color-secondary-text)] truncate mb-2">
          {transformation.name}
        </h3>

        {/* Third Line: Description */}
        {transformation.description && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {transformation.description}
          </p>
        )}

        {/* Metadata */}
        <div className="space-y-2 mb-3 flex-1">
          <div className="flex items-center justify-between gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
            {(transformation.start_date || transformation.end_date) ? (
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                <span>
                  {transformation.start_date && new Date(transformation.start_date).toLocaleDateString()}
                  {transformation.start_date && transformation.end_date && ' - '}
                  {transformation.end_date && new Date(transformation.end_date).toLocaleDateString()}
                </span>
              </div>
            ) : (
              <div></div>
            )}
            {/* Action Icons - After Dates */}
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {/* Edit Button (Project settings when editActionIcon is Cog6ToothIcon) - Always shown */}
              {(canEdit ? canEdit(transformation) : true) && (
                <button
                  onClick={handleEdit}
                  className="p-1.5 text-[var(--color-success)] hover:bg-[var(--color-success)]/20 rounded-md transition-colors"
                  title={editActionLabel || t('common.edit')}
                  type="button"
                >
                  {React.createElement(editActionIcon || PencilIcon, { className: 'w-4 h-4' })}
                </button>
              )}
              {/* View Button */}
              {onView && (
                <button
                  onClick={(e) => handleActionClick(e, () => onView(transformation))}
                  className="p-1.5 text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-md transition-colors"
                  title={t('common.view')}
                  type="button"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              )}
              {/* Copy Button */}
              {onCopy && (
                <button
                  onClick={(e) => handleActionClick(e, () => onCopy(transformation))}
                  className="p-1.5 text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-md transition-colors"
                  title={t('common.copy')}
                  type="button"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              )}
              {/* Delete Button */}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, () => onDelete(transformation))}
                  className="p-1.5 text-[var(--color-error)] hover:bg-[var(--color-error)]/20 rounded-md transition-colors"
                  title={t('common.delete')}
                  type="button"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              {/* Custom Actions */}
              {customActions.map((action, index) => {
                const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                  ? (action.icon as (item: any) => React.ComponentType<any>)(transformation)
                  : action.icon as React.ComponentType<any>;
                const variant = typeof action.variant === 'function' 
                  ? action.variant(transformation) 
                  : action.variant || 'primary';
                const colorClass = variant === 'danger' 
                  ? 'text-[var(--color-error)] hover:bg-[var(--color-error)]/20' 
                  : variant === 'secondary' 
                  ? 'text-[var(--color-secondary-text)] hover:bg-[var(--color-secondary-bg)]' 
                  : 'text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20';
                
                return (
                  <button
                    key={index}
                    onClick={(e) => handleActionClick(e, () => action.onClick(transformation))}
                    className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                    title={typeof action.label === 'function' ? action.label(transformation) : action.label}
                    type="button"
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-auto pt-3 border-t border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FlagIcon className="w-4 h-4 text-[var(--color-info)]" />
              <span className="text-xs font-medium text-[var(--color-secondary-text)]">
                Progress
              </span>
              {hasDelayedInchstones && (
                <ExclamationTriangleIcon className="w-4 h-4 text-[var(--color-warning)]" title="Delayed inchstones" />
              )}
            </div>
            <span className="text-xs font-semibold text-[var(--color-secondary-text)]">
              {completionPercentage.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-[var(--color-border)] rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${
                hasDelayedInchstones ? 'bg-[var(--color-warning)]' : 'bg-[var(--color-accent-bg)]'
              }`}
              style={{ width: `${Math.min(100, completionPercentage)}%` }}
            />
          </div>
          {transformation.inchstone_stats && (
            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <span>
                {transformation.inchstone_stats.completed_inchstones} / {transformation.inchstone_stats.total_inchstones} inchstones
              </span>
              {transformation.inchstone_stats.delayed_inchstones > 0 && (
                <span className="text-yellow-600">
                  {transformation.inchstone_stats.delayed_inchstones} delayed
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

